import * as THREE from "three";
import type { ActiveImpact, PresentationFrame, SampledEntity, Vec3 } from "../core";
import { resolveTargetFacingYaw, worldDirectionToLocal } from "../motion";
import {
  projectPresentationImpulses,
  sampleSecondaryMotion,
  type PresentationImpulse,
} from "../physics";
import {
  resolveCharacterMotion,
  resolveClassPresentationKit,
  resolveIncomingImpact,
  type CharacterImpactSample,
  type CharacterRig,
  type ClassPresentationKit,
} from "./class-kits";
import type { Palette, RenderStyle } from "./styles";
import { SecondaryEnvironment } from "./secondary-environment";

interface ActorVisual {
  kind: "actor";
  group: THREE.Group;
  kit: ClassPresentationKit;
  appearanceKey: string;
  rig: CharacterRig;
  aura: THREE.Mesh;
  telegraph: THREE.Mesh;
  groundShadow: THREE.Mesh;
  teamRing: THREE.Mesh;
  materials: THREE.MeshToonMaterial[];
}

interface PropVisual {
  kind: "prop";
  group: THREE.Group;
  materials: THREE.MeshToonMaterial[];
  animatedPart?: THREE.Object3D;
  animatedBaseY?: number;
}

type EntityVisual = ActorVisual | PropVisual;

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of materials) material.dispose();
  });
}

function makeGradientMap(bands: number): THREE.DataTexture {
  const count = Math.max(2, Math.round(bands));
  const data = new Uint8Array(count * 4);
  for (let i = 0; i < count; i += 1) {
    const value = Math.round((i / (count - 1)) * 255);
    data[i * 4] = value;
    data[i * 4 + 1] = value;
    data[i * 4 + 2] = value;
    data[i * 4 + 3] = 255;
  }
  const texture = new THREE.DataTexture(data, count, 1, THREE.RGBAFormat);
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.needsUpdate = true;
  return texture;
}

function appearanceKey(entity: SampledEntity): string {
  return JSON.stringify(entity.appearance?.items ?? null);
}

function appearanceColor(entity: SampledEntity, slot: "body" | "accent" | "dark", fallback: number): number {
  const candidate = entity.appearance?.colors?.[slot];
  return candidate && /^#[0-9a-f]{6}$/i.test(candidate) ? Number.parseInt(candidate.slice(1), 16) : fallback;
}

export class LabRenderer {
  readonly canvas: HTMLCanvasElement;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-8, 8, 5, -5, 0.1, 100);
  private readonly world = new THREE.Group();
  private readonly secondaryEnvironment = new SecondaryEnvironment();
  private readonly entityVisuals = new Map<string, EntityVisual>();
  private readonly impactVisuals = new Map<string, THREE.Mesh>();
  private readonly targetLines = new Map<string, THREE.Line>();
  private readonly resizeObserver: ResizeObserver;
  private readonly floorMaterial: THREE.MeshToonMaterial;
  private readonly arenaEdgeMaterial: THREE.MeshToonMaterial;
  private readonly playerFieldMaterial: THREE.MeshBasicMaterial;
  private readonly enemyFieldMaterial: THREE.MeshBasicMaterial;
  private readonly gridMaterials: THREE.Material[];
  private readonly keyLight: THREE.DirectionalLight;
  private gradientMap = makeGradientMap(4);
  private lightingBands = 4;
  private currentSourceId = "";
  private width = 1;
  private height = 1;
  private cameraZoom = 5.3;

  constructor(private readonly mount: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: "high-performance" });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = false;
    this.canvas = this.renderer.domElement;
    this.canvas.className = "stage-canvas";
    this.canvas.setAttribute("aria-label", "Trace-driven 3D presentation stage");
    mount.append(this.canvas);

    this.scene.add(this.world);
    this.world.add(this.secondaryEnvironment.root);
    this.floorMaterial = new THREE.MeshToonMaterial({ color: 0x25202a, gradientMap: this.gradientMap });
    this.arenaEdgeMaterial = new THREE.MeshToonMaterial({ color: 0x17131b, gradientMap: this.gradientMap });
    this.playerFieldMaterial = new THREE.MeshBasicMaterial({ color: 0x3fa7d6, transparent: true, opacity: 0.045, side: THREE.DoubleSide });
    this.enemyFieldMaterial = new THREE.MeshBasicMaterial({ color: 0xdb4b56, transparent: true, opacity: 0.045, side: THREE.DoubleSide });

    const arenaBase = new THREE.Mesh(new THREE.BoxGeometry(14.6, 0.28, 9.6), this.arenaEdgeMaterial);
    arenaBase.position.y = -0.18;
    this.world.add(arenaBase);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14.2, 9.2), this.floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.025;
    this.world.add(floor);

    const playerField = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 9.1), this.playerFieldMaterial);
    playerField.rotation.x = -Math.PI / 2;
    playerField.position.set(-4.78, -0.012, 0);
    this.world.add(playerField);
    const enemyField = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 9.1), this.enemyFieldMaterial);
    enemyField.rotation.x = -Math.PI / 2;
    enemyField.position.set(4.78, -0.011, 0);
    this.world.add(enemyField);

    const gridMaterial = new THREE.LineBasicMaterial({ color: 0x413243, transparent: true, opacity: 0.29 });
    const gridPoints: THREE.Vector3[] = [];
    for (let x = -7; x <= 7.001; x += 0.5) {
      gridPoints.push(new THREE.Vector3(x, 0, -4.5), new THREE.Vector3(x, 0, 4.5));
    }
    for (let z = -4.5; z <= 4.501; z += 0.5) {
      gridPoints.push(new THREE.Vector3(-7, 0, z), new THREE.Vector3(7, 0, z));
    }
    const gridGeometry = new THREE.BufferGeometry().setFromPoints(gridPoints);
    const grid = new THREE.LineSegments(gridGeometry, gridMaterial);
    this.gridMaterials = [gridMaterial];
    for (const material of this.gridMaterials) {
      material.transparent = true;
      material.opacity = 0.29;
    }
    this.world.add(grid);

    const centerLineMaterial = new THREE.MeshBasicMaterial({ color: 0xffd166, transparent: true, opacity: 0.12, side: THREE.DoubleSide });
    const centerLine = new THREE.Mesh(new THREE.PlaneGeometry(0.035, 9), centerLineMaterial);
    centerLine.rotation.x = -Math.PI / 2;
    centerLine.position.y = 0.006;
    this.world.add(centerLine);

    const railGeometryLong = new THREE.BoxGeometry(14.55, 0.22, 0.12);
    const railGeometryShort = new THREE.BoxGeometry(0.12, 0.22, 9.55);
    for (const [x, z, geometry] of [
      [0, -4.72, railGeometryLong],
      [0, 4.72, railGeometryLong],
      [-7.22, 0, railGeometryShort],
      [7.22, 0, railGeometryShort],
    ] as const) {
      const rail = new THREE.Mesh(geometry, this.arenaEdgeMaterial);
      rail.position.set(x, 0.05, z);
      this.world.add(rail);
    }

    this.scene.add(new THREE.HemisphereLight(0xddeeff, 0x1a1020, 1.35));
    this.keyLight = new THREE.DirectionalLight(0xffe2b8, 2.4);
    this.keyLight.position.set(-5, 10, 7);
    this.scene.add(this.keyLight);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(mount);
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    for (const visual of this.entityVisuals.values()) disposeObject(visual.group);
    for (const visual of this.impactVisuals.values()) disposeObject(visual);
    this.secondaryEnvironment.dispose();
    this.gradientMap.dispose();
    this.renderer.dispose();
    this.canvas.remove();
  }

  render(frame: PresentationFrame, sourceId: string, style: RenderStyle): void {
    if (this.currentSourceId !== sourceId) {
      this.resetEntities();
      this.currentSourceId = sourceId;
    }

    this.applyStyle(style);
    const impulses = projectPresentationImpulses(frame);
    this.secondaryEnvironment.update(frame.time, impulses, style.windStrength, style.palette);
    this.syncEntities(frame, style, impulses);
    this.syncImpacts(frame, style.palette);
    this.resize(style.pixelScale, style.cameraZoom);
    this.renderer.render(this.scene, this.camera);
  }

  /** Project a world-space point into the full-resolution stage overlay. */
  projectToStage(position: Vec3): { x: number; y: number; visible: boolean } {
    const projected = new THREE.Vector3(position.x, position.y, position.z).project(this.camera);
    const width = this.mount.clientWidth;
    const height = this.mount.clientHeight;
    return {
      x: (projected.x * 0.5 + 0.5) * width,
      y: (-projected.y * 0.5 + 0.5) * height,
      visible: projected.z >= -1 && projected.z <= 1 && projected.x >= -1.08 && projected.x <= 1.08 && projected.y >= -1.08 && projected.y <= 1.08,
    };
  }

  private resetEntities(): void {
    for (const visual of this.entityVisuals.values()) {
      this.world.remove(visual.group);
      disposeObject(visual.group);
    }
    for (const visual of this.impactVisuals.values()) {
      this.world.remove(visual);
      disposeObject(visual);
    }
    for (const line of this.targetLines.values()) {
      this.world.remove(line);
      disposeObject(line);
    }
    this.entityVisuals.clear();
    this.impactVisuals.clear();
    this.targetLines.clear();
  }

  private applyStyle(style: RenderStyle): void {
    const requestedBands = Math.max(2, Math.round(style.lightingBands));
    if (requestedBands !== this.lightingBands) {
      const previous = this.gradientMap;
      this.gradientMap = makeGradientMap(requestedBands);
      this.lightingBands = requestedBands;
      this.floorMaterial.gradientMap = this.gradientMap;
      this.floorMaterial.needsUpdate = true;
      this.arenaEdgeMaterial.gradientMap = this.gradientMap;
      this.arenaEdgeMaterial.needsUpdate = true;
      for (const visual of this.entityVisuals.values()) {
        for (const material of visual.materials) {
          material.gradientMap = this.gradientMap;
          material.needsUpdate = true;
        }
      }
      previous.dispose();
    }
    this.scene.background = new THREE.Color(style.palette.background);
    this.floorMaterial.color.setHex(style.palette.floor);
    this.arenaEdgeMaterial.color.setHex(style.palette.background).multiplyScalar(0.72);
    this.playerFieldMaterial.color.setHex(style.palette.player);
    this.enemyFieldMaterial.color.setHex(style.palette.enemy);
    for (const material of this.gridMaterials) {
      if (material instanceof THREE.LineBasicMaterial) material.color.setHex(style.palette.floorAccent);
    }

    const pitch = THREE.MathUtils.degToRad(style.cameraPitch);
    const radius = 17;
    const orbit = THREE.MathUtils.degToRad(style.cameraYaw);
    const horizontal = Math.cos(pitch) * radius;
    const target = style.cameraTarget;
    this.camera.position.set(
      target.x + Math.sin(orbit) * horizontal,
      target.y + Math.sin(pitch) * radius,
      target.z + Math.cos(orbit) * horizontal,
    );
    this.camera.lookAt(target.x, target.y, target.z);
    this.camera.updateProjectionMatrix();
  }

  private syncEntities(frame: PresentationFrame, style: RenderStyle, secondaryImpulses: readonly PresentationImpulse[]): void {
    const { entities, impacts, time } = frame;
    const byId = new Map(entities.map((entity) => [entity.id, entity]));
    for (const [id, visual] of this.entityVisuals) {
      if (byId.has(id)) continue;
      this.world.remove(visual.group);
      disposeObject(visual.group);
      this.entityVisuals.delete(id);
      const line = this.targetLines.get(id);
      if (line) {
        this.world.remove(line);
        disposeObject(line);
        this.targetLines.delete(id);
      }
    }
    for (const entity of entities) {
      let visual = this.entityVisuals.get(entity.id);
      if (visual?.kind === "actor" && (
        visual.kit.id !== resolveClassPresentationKit(entity.presentationKit).id
        || visual.appearanceKey !== appearanceKey(entity)
      )) {
        this.world.remove(visual.group);
        disposeObject(visual.group);
        this.entityVisuals.delete(entity.id);
        visual = undefined;
      }
      if (!visual) {
        visual = entity.kind === "actor" ? this.createActor(entity, style) : this.createProp(entity, style);
        this.entityVisuals.set(entity.id, visual);
        this.world.add(visual.group);
      }
      visual.group.visible = entity.visible;
      visual.group.position.set(entity.position.x, entity.position.y, entity.position.z);
      visual.group.rotation.set(entity.rotation?.x ?? 0, entity.rotation?.y ?? 0, entity.rotation?.z ?? 0);
      this.updateVisualPalette(visual, entity, style.palette);
      if (visual.kind === "actor") this.poseActor(visual, entity, byId, impacts, style, time, secondaryImpulses);
      else this.animateProp(visual, entity, time);
    }
  }

  private updateVisualPalette(visual: EntityVisual, entity: SampledEntity, palette: Palette): void {
    if (visual.kind === "actor") {
      const bodyFallback = entity.team === "player" ? palette.player : palette.enemy;
      const accentFallback = entity.team === "player" ? palette.playerAccent : palette.enemyAccent;
      visual.rig.materials.body.color.setHex(appearanceColor(entity, "body", bodyFallback));
      visual.rig.materials.accent.color.setHex(appearanceColor(entity, "accent", accentFallback));
      visual.rig.materials.dark.color.setHex(appearanceColor(entity, "dark", 0x16141a));
      (visual.aura.material as THREE.MeshBasicMaterial).color.setHex(palette.highlight);
      (visual.telegraph.material as THREE.MeshBasicMaterial).color.setHex(palette.highlight);
      (visual.teamRing.material as THREE.MeshBasicMaterial).color.setHex(entity.team === "player" ? palette.playerAccent : palette.enemyAccent);
    } else {
      visual.materials[0]?.color.setHex(entity.team === "player" ? palette.player : entity.team === "enemy" ? palette.enemy : palette.neutral);
      if (entity.archetype === "cover") {
        const capColor = new THREE.Color(palette.neutral).lerp(new THREE.Color(palette.highlight), 0.24);
        visual.materials[1]?.color.copy(capColor);
      } else {
        visual.materials[1]?.color.setHex(entity.team === "player" ? palette.playerAccent : entity.team === "enemy" ? palette.enemyAccent : palette.highlight);
      }
    }
  }

  private createMaterial(color: number): THREE.MeshToonMaterial {
    return new THREE.MeshToonMaterial({ color, gradientMap: this.gradientMap });
  }

  private createActor(entity: SampledEntity, style: RenderStyle): ActorVisual {
    const group = new THREE.Group();
    group.scale.setScalar(entity.scale ?? 1);
    const bodyColor = appearanceColor(entity, "body", entity.team === "player" ? style.palette.player : style.palette.enemy);
    const accentColor = appearanceColor(entity, "accent", entity.team === "player" ? style.palette.playerAccent : style.palette.enemyAccent);
    const body = this.createMaterial(bodyColor);
    const accent = this.createMaterial(accentColor);
    const dark = this.createMaterial(appearanceColor(entity, "dark", 0x16141a));
    const materials = [body, accent, dark];
    const kit = resolveClassPresentationKit(entity.presentationKit);
    const rig = kit.create(entity, { body, accent, dark });

    const shadowMaterial = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.28, depthWrite: false });
    const groundShadow = new THREE.Mesh(new THREE.CircleGeometry(0.58, 18), shadowMaterial);
    groundShadow.rotation.x = -Math.PI / 2;
    groundShadow.scale.set(1, 0.58, 1);
    groundShadow.position.y = 0.012;
    group.add(groundShadow);

    const ringMaterial = new THREE.MeshBasicMaterial({ color: accentColor, transparent: true, opacity: 0.7, side: THREE.DoubleSide, depthWrite: false });
    const teamRing = new THREE.Mesh(new THREE.RingGeometry(0.52, 0.61, 24), ringMaterial);
    teamRing.rotation.x = -Math.PI / 2;
    teamRing.position.y = 0.018;
    group.add(teamRing);
    group.add(rig.modelRoot);

    const auraMaterial = new THREE.MeshBasicMaterial({ color: style.palette.highlight, transparent: true, opacity: 0.24, side: THREE.DoubleSide });
    const aura = new THREE.Mesh(new THREE.RingGeometry(0.52, 0.68, 20), auraMaterial);
    aura.rotation.x = -Math.PI / 2;
    aura.position.y = 0.025;
    aura.visible = false;
    group.add(aura);

    const telegraphMaterial = new THREE.MeshBasicMaterial({ color: style.palette.highlight, transparent: true, opacity: 0.48, side: THREE.DoubleSide });
    const telegraph = new THREE.Mesh(new THREE.RingGeometry(0.78, 0.88, 24), telegraphMaterial);
    telegraph.rotation.x = -Math.PI / 2;
    telegraph.position.y = 0.035;
    telegraph.visible = false;
    group.add(telegraph);

    return {
      kind: "actor",
      group,
      kit,
      appearanceKey: appearanceKey(entity),
      rig,
      aura,
      telegraph,
      groundShadow,
      teamRing,
      materials,
    };
  }

  private createProp(entity: SampledEntity, style: RenderStyle): PropVisual {
    const group = new THREE.Group();
    if (entity.archetype !== "cover") group.scale.setScalar(entity.scale ?? 1);
    const mainColor = entity.team === "player" ? style.palette.player : entity.team === "enemy" ? style.palette.enemy : style.palette.neutral;
    const accentColor = entity.team === "player" ? style.palette.playerAccent : entity.team === "enemy" ? style.palette.enemyAccent : style.palette.highlight;
    const material = this.createMaterial(mainColor);
    const accent = this.createMaterial(accentColor);
    let animatedPart: THREE.Object3D | undefined;
    let animatedBaseY: number | undefined;
    if (entity.archetype === "wardstone") {
      const haloMaterial = new THREE.MeshBasicMaterial({ color: accentColor, transparent: true, opacity: 0.34, side: THREE.DoubleSide, depthWrite: false });
      const halo = new THREE.Mesh(new THREE.RingGeometry(0.92, 1.05, 28), haloMaterial);
      halo.rotation.x = -Math.PI / 2;
      halo.position.y = 0.018;
      group.add(halo);
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.92, 0.32, 8), material);
      base.position.y = 0.16;
      group.add(base);
      const stone = new THREE.Mesh(new THREE.OctahedronGeometry(0.58, 0), accent);
      stone.position.y = 0.95;
      stone.rotation.z = 0.22;
      group.add(stone);
      animatedPart = stone;
      animatedBaseY = stone.position.y;
    } else if (entity.archetype === "projectile") {
      const glowMaterial = new THREE.MeshBasicMaterial({ color: accentColor, transparent: true, opacity: 0.24, depthWrite: false });
      const glow = new THREE.Mesh(new THREE.SphereGeometry(0.62, 8, 6), glowMaterial);
      group.add(glow);
      const shot = new THREE.Mesh(new THREE.OctahedronGeometry(0.34, 0), accent);
      shot.rotation.z = Math.PI / 4;
      group.add(shot);
      animatedPart = shot;
      animatedBaseY = shot.position.y;
    } else if (entity.kind === "zone") {
      const zoneMaterial = new THREE.MeshBasicMaterial({ color: accentColor, transparent: true, opacity: 0.2, side: THREE.DoubleSide });
      const zone = new THREE.Mesh(new THREE.CircleGeometry(1, 28), zoneMaterial);
      zone.rotation.x = -Math.PI / 2;
      zone.position.y = 0.02;
      group.add(zone);
    } else {
      const dimensions = entity.dimensions ?? { x: 1.2, y: 0.72, z: 0.72 };
      const cover = new THREE.Mesh(new THREE.BoxGeometry(dimensions.x, dimensions.y, dimensions.z), material);
      cover.position.y = dimensions.y / 2;
      group.add(cover);
      const cap = new THREE.Mesh(new THREE.BoxGeometry(dimensions.x * 0.94, 0.08, dimensions.z * 0.94), accent);
      cap.position.y = dimensions.y + 0.025;
      group.add(cap);
      const footing = new THREE.Mesh(new THREE.BoxGeometry(dimensions.x * 1.08, 0.08, dimensions.z * 1.08), material);
      footing.position.y = 0.04;
      group.add(footing);
    }
    return { kind: "prop", group, materials: [material, accent], animatedPart, animatedBaseY };
  }

  private animateProp(visual: PropVisual, entity: SampledEntity, time: number): void {
    const part = visual.animatedPart;
    if (!part) return;
    part.rotation.y = time * (entity.archetype === "projectile" ? 8 : 1.2);
    part.position.y = (visual.animatedBaseY ?? 0) + Math.sin(time * 3 + entity.position.x) * (entity.archetype === "projectile" ? 0.05 : 0.08);
  }

  private poseActor(
    visual: ActorVisual,
    entity: SampledEntity,
    entities: Map<string, SampledEntity>,
    impacts: ActiveImpact[],
    style: RenderStyle,
    time: number,
    secondaryImpulses: readonly PresentationImpulse[],
  ): void {
    const motion = resolveCharacterMotion(entity, impacts);
    const target = entity.action?.targetId ? entities.get(entity.action.targetId) : undefined;
    const facingYaw = resolveTargetFacingYaw(entity.position, target?.position, entity.rotation?.y ?? 0);
    visual.group.rotation.y = facingYaw;
    const incoming = resolveIncomingImpact(entity, impacts);
    visual.kit.pose(visual.rig, {
      entity,
      motion,
      time,
      exaggeration: style.motionExaggeration,
      impact: incoming ? impactSample(incoming, secondaryImpulses, facingYaw) : undefined,
    });
    this.poseSecondaryBodies(visual.rig, entity, facingYaw, time, secondaryImpulses, style.windStrength);
    visual.aura.visible = entity.statuses.length > 0;
    visual.aura.rotation.z += 0.015;
    visual.telegraph.visible = false;
    const existingTargetLine = this.targetLines.get(entity.id);
    if (existingTargetLine) existingTargetLine.visible = false;

    if (entity.action?.phase === "telegraph") {
      const p = entity.action.phaseProgress;
      visual.telegraph.visible = true;
      visual.telegraph.scale.setScalar(0.65 + p * 0.45);
      visual.telegraph.rotation.z = p * Math.PI * 0.5;
    }
    if (target) this.syncTargetLine(entity, target, visual, style.palette);
  }

  private poseSecondaryBodies(
    rig: CharacterRig,
    entity: SampledEntity,
    facingYaw: number,
    time: number,
    impulses: readonly PresentationImpulse[],
    windStrength: number,
  ): void {
    const position = { x: entity.position.x, y: entity.position.y + 1.7, z: entity.position.z };
    for (const body of rig.secondaryBodies) {
      const sample = sampleSecondaryMotion({ position, time, impulses, response: body.response, windStrength });
      const local = worldDirectionToLocal(sample.displacement, facingYaw);
      const base = body.additiveToPose
        ? { x: body.object.rotation.x, y: body.object.rotation.y, z: body.object.rotation.z }
        : body.restRotation;
      body.object.rotation.set(
        base.x - local.z * body.rotationScale,
        base.y,
        base.z + local.x * body.rotationScale,
      );
    }
  }

  private syncTargetLine(entity: SampledEntity, target: SampledEntity, visual: ActorVisual, palette: Palette): void {
    let line = this.targetLines.get(entity.id);
    if (!line) {
      const geometry = new THREE.BufferGeometry();
      const material = new THREE.LineDashedMaterial({ color: palette.highlight, transparent: true, opacity: 0.55, dashSize: 0.18, gapSize: 0.12 });
      line = new THREE.Line(geometry, material);
      this.targetLines.set(entity.id, line);
      this.world.add(line);
    }
    line.visible = visual.telegraph.visible;
    line.geometry.setFromPoints([
      new THREE.Vector3(entity.position.x, 0.16, entity.position.z),
      new THREE.Vector3(target.position.x, 0.16, target.position.z),
    ]);
    line.computeLineDistances();
  }

  private syncImpacts(frame: PresentationFrame, palette: Palette): void {
    const activeIds = new Set(frame.impacts.map((impact) => impact.eventId));
    for (const [id, visual] of this.impactVisuals) {
      if (activeIds.has(id)) continue;
      this.world.remove(visual);
      disposeObject(visual);
      this.impactVisuals.delete(id);
    }

    for (const impact of frame.impacts) {
      const target = frame.entities.find((entity) => entity.id === impact.targetId);
      if (!target) continue;
      let visual = this.impactVisuals.get(impact.eventId);
      if (!visual) {
        const color = impact.outcome === "block"
          ? palette.block
          : impact.outcome === "heal"
            ? palette.heal
            : impact.outcome === "miss"
              ? palette.miss
              : palette.hit;
        const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, side: THREE.DoubleSide });
        const segments = impact.outcome === "heal" ? 20 : impact.outcome === "block" ? 6 : 8;
        visual = new THREE.Mesh(new THREE.RingGeometry(0.24, 0.38, segments), material);
        visual.rotation.x = -Math.PI / 2;
        this.impactVisuals.set(impact.eventId, visual);
        this.world.add(visual);
      }
      const progress = Math.min(1, impact.age / 0.42);
      visual.position.set(target.position.x, 0.08, target.position.z);
      visual.scale.setScalar(1 + progress * 1.7);
      (visual.material as THREE.MeshBasicMaterial).opacity = 1 - progress;
      visual.rotation.z = progress * Math.PI * 0.75;
    }
  }

  private resize(pixelScale = 4, cameraZoom = this.cameraZoom): void {
    const rect = this.mount.getBoundingClientRect();
    const displayWidth = Math.max(1, Math.floor(rect.width));
    const displayHeight = Math.max(1, Math.floor(rect.height));
    const internalWidth = Math.max(240, Math.floor(displayWidth / pixelScale));
    const internalHeight = Math.max(150, Math.floor(displayHeight / pixelScale));
    const dimensionsChanged = internalWidth !== this.width || internalHeight !== this.height;
    const zoomChanged = cameraZoom !== this.cameraZoom;
    if (!dimensionsChanged && !zoomChanged) return;
    if (dimensionsChanged) {
      this.width = internalWidth;
      this.height = internalHeight;
      this.renderer.setSize(internalWidth, internalHeight, false);
      this.canvas.style.width = `${displayWidth}px`;
      this.canvas.style.height = `${displayHeight}px`;
    }
    this.cameraZoom = cameraZoom;
    const aspect = displayWidth / displayHeight;
    const vertical = cameraZoom;
    this.camera.left = -vertical * aspect;
    this.camera.right = vertical * aspect;
    this.camera.top = vertical;
    this.camera.bottom = -vertical;
    this.camera.updateProjectionMatrix();
  }
}

function impactSample(impact: ActiveImpact, impulses: readonly PresentationImpulse[], facingYaw: number): CharacterImpactSample {
  const projected = impulses.find((candidate) => candidate.id === impact.eventId);
  const localDirection = projected
    ? worldDirectionToLocal(projected.direction, facingYaw)
    : { x: 0, y: 0, z: -1 };
  return {
    direction: localDirection,
    strength: projected?.magnitude ?? 0.68,
    pointHeight: projected?.pointHeight ?? 0.58,
  };
}
